"use client";

import { cn } from "@/utils/cn";
import Link from "next/link";
import { ReactNode } from "react";

interface PromotionalCardBaseProps {
	icon: ReactNode;
	title: string;
	description: string;
	ctaText?: string;
	onClick?: () => void;
	href?: string;
	className?: string;
	iconColor?: string;
	isExternal?: boolean;
	children?: ReactNode;
}

export function PromotionalCardBase({
	icon,
	title,
	description,
	ctaText,
	onClick,
	href,
	className,
	iconColor = "text-primary-600",
	children,
	isExternal,
}: PromotionalCardBaseProps) {
	const external = isExternal ?? href?.startsWith("http");

	const content = (
		<div
			className={cn(
				"flex items-center gap-4 px-4 py-3",
				"bg-gray-50/80 rounded-lg",
				"border-l-2 border-primary-400",
				className,
			)}
		>
			{/* Icon */}
			<div className={cn("w-8 h-8 flex items-center justify-center shrink-0", iconColor)}>{icon}</div>

			{/* Content */}
			<div className="flex-1 min-w-0">
				<span className="text-sm font-medium text-gray-800">{title}</span>
				<span className="text-gray-400 mx-2">·</span>
				<span className="text-xs text-gray-500">{description}</span>
				{children}
			</div>

			{/* CTA */}
			{ctaText && <span className="text-xs font-medium text-primary-600 shrink-0 hidden sm:inline">{ctaText} →</span>}
		</div>
	);

	if (href) {
		if (external) {
			return (
				<a href={href} target="_blank" rel="noopener noreferrer" onClick={onClick} className="block w-full">
					{content}
				</a>
			);
		}

		return (
			<Link href={href} onClick={onClick} className="block w-full">
				{content}
			</Link>
		);
	}

	return (
		<div
			role="button"
			tabIndex={0}
			onClick={onClick}
			onKeyDown={e => {
				if (e.key === "Enter" || e.key === " ") onClick?.();
			}}
			className="cursor-pointer w-full"
		>
			{content}
		</div>
	);
}
